<template>
  <div>
    <div class="mb-4 flex justify-end space-x-2 py-2">
      <MerchantGroupMerchantFilter v-model="schemaName" />
    </div>

    <BaseCard class="relative">
      <div class="mb-2 flex w-full">
        <SearchInput v-model="rawSearch" />
      </div>
      <BaseTable
        v-model:ordering="ordering"
        :rows="data?.items ?? []"
        :columns="columns"
        :is-loading="isLoading"
      >
        <template
          #cell-name="{
            row,
          }: {
            row: MerchantGroupTerminal
          }"
        >
          <BaseTableChevronLink
            v-if="row.schemaName"
            :to="{
              name: 'merchantGroupMerchantDetail',
              params: {
                schemaName: row.schemaName,
              },
              query: {
                initialTab: 'Terminals',
              },
            }"
          >
            {{ row.clientName }}
          </BaseTableChevronLink>
          <span v-else>
            {{ row.clientName }}
          </span>
        </template>
        <template
          #cell-lastPaired="{
            row,
          }: {
            row: MerchantGroupTerminal
          }"
        >
          <template v-if="row.lastPaired">
            <div>
              <p class="text-sm text-background-fg/87">
                {{ formatToDate(row.lastPaired) }} at
                {{ formatToTime(row.lastPaired) }}
              </p>
              <p class="text-xs text-background-fg/70">
                {{
                  DateFns.formatDistance(
                    row.lastPaired,
                    now,
                    { addSuffix: true }
                  )
                }}
              </p>
            </div>
          </template>
        </template>
      </BaseTable>

      <div class="flex justify-end py-2">
        <BasePagination
          v-model:page="page"
          :page-count="pageCount"
        />
      </div>
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"
import type {
  ListMerchantGroupTerminalsFilter,
  ListMerchantGroupTerminalsOrdering,
  ListMerchantGroupTerminalsQuery,
} from "@/services/merchantGroup/terminals/listMerchantGroupTerminals"
import type { MerchantGroupTerminal } from "@/services/merchantGroup/terminals/listMerchantGroupTerminals"

const rawSearch = ref("")
const debouncedSearch = debouncedRef(rawSearch, 300)

const schemaName = ref<string | undefined>()

const filters = computed<ListMerchantGroupTerminalsFilter>(
  () => ({
    search: debouncedSearch.value.trim(),
    schemaName: schemaName.value,
  })
)

const {
  page,
  pageCount,
  paginationQuery,
  resetPagination,
} = usePagination({
  count: computed(() => data.value?.count || 0),
  limit: 10,
})

const ordering =
  ref<TableOrdering<ListMerchantGroupTerminalsOrdering>>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)

const query =
  reactiveComputed<ListMerchantGroupTerminalsQuery>(() => ({
    ...filters.value,
    ordering: ordering.value,
    ...paginationQuery.value,
  }))

const { data, isLoading } = useMerchantGroupTerminals(query)

const now = useNow()

type ColumnId =
  | "terminalId"
  | "name"
  | "lastPaired"
  | "lastPairedVersion"
  | "registrationDate"
const columns: Array<
  TableColumn<
    MerchantGroupTerminal,
    ColumnId,
    ListMerchantGroupTerminalsOrdering
  >
> = [
  {
    id: "terminalId",
    header: "Serial Number",
    getCellValue: (row) => row.terminalId,
    isSortable: true,
  },
  {
    id: "name",
    header: "Client Name",
    getCellValue: (row) => row.clientName,
    isSortable: true,
  },
  {
    id: "lastPaired",
    header: "Last Paired",
    getCellValue: (row) => row.lastPaired,
    isSortable: true,
  },
  {
    id: "lastPairedVersion",
    header: "Last Paired Version",
    getCellValue: (row) => row.lastPairedVersion,
    isSortable: true,
  },
  {
    id: "registrationDate",
    header: "Registered",
    getCellValue: (row) =>
      row.registrationDate
        ? DateFns.formatDistance(
            row.registrationDate,
            now.value,
            {
              addSuffix: true,
            }
          )
        : "",
    isSortable: true,
  },
]
</script>
